import React, { useState, useEffect } from 'react';
import { Building2, Users, Calendar, Loader2 } from 'lucide-react';
import { jobsApi } from '../../api/jobs';
import { Job as ApiJob } from '../../api/jobs';
import { Job as MockJob } from '../../data/mockJobs';
import { candidatesApi } from '../../api/candidates';
import { useLanguage } from '../../contexts/LanguageContext';
import { useDataRefresh } from '../../contexts/DataRefreshContext';

// Create a union type that can handle both job formats
type Job = ApiJob | MockJob;

interface JobSelectProps {
  selectedJobId: string;
  onJobChange: (jobId: string) => void;
  jobStatus?: 'ACTIVE' | 'COMPLETED' | 'ALL';
  onStatusChange?: (status: 'ACTIVE' | 'COMPLETED' | 'ALL') => void;
}

const JobSelect = ({ selectedJobId, onJobChange, jobStatus = 'ACTIVE', onStatusChange }: JobSelectProps) => {
  const { t } = useLanguage();
  const { refreshCounters, refreshJobs } = useDataRefresh();
  const [jobs, setJobs] = useState<Job[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [refreshing, setRefreshing] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [jobCandidateCounts, setJobCandidateCounts] = useState<Record<string, number>>({});

  // Fetch jobs when the component mounts
  useEffect(() => {
    const fetchJobs = async () => {
      setLoading(true);
      setError(null);
      
      try {
        const jobsData = await jobsApi.getAll();
        setJobs(jobsData);
      } catch (err) {
        console.error('Failed to fetch jobs:', err);
        setError(t('candidates.jobSelect.error'));
      } finally {
        setLoading(false);
      }
    };
    
    fetchJobs();
  }, [refreshCounters.jobs]); // Refetch jobs when the jobs refresh counter changes

  // Fetch candidate counts for each job
  useEffect(() => {
    const fetchCandidateCounts = async () => {
      if (jobs.length === 0) return;
      
      try {
        // Get all candidates
        const candidates = await candidatesApi.getAll();
        
        // Count candidates for each job
        const counts: Record<string, number> = {};
        jobs.forEach(job => {
          counts[job.id] = candidates.filter(c => c.jobId === job.id).length;
        });
        
        setJobCandidateCounts(counts);
      } catch (err) {
        console.error('Failed to fetch candidate counts:', err);
      }
    };
    
    fetchCandidateCounts();
  }, [jobs, refreshCounters.candidates]); // Also refresh candidate counts when candidate data changes

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <div className="flex items-center gap-2 text-gray-500">
          <Loader2 className="w-5 h-5 animate-spin" />
          <span>{t('candidates.jobSelect.loading')}</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 text-red-600 p-4 rounded-lg">
        {error}
      </div>
    );
  }

  // Filter jobs based on selected status
  const filteredJobs = jobs.filter(job => {
    if (jobStatus === 'ALL') return true;
    if (jobStatus === 'ACTIVE') return job.status === 'ACTIVE' || !job.status;
    if (jobStatus === 'COMPLETED') return job.status === 'COMPLETED';
    return true;
  });

  // Get the selected job details
  const selectedJob = jobs.find(job => job.id === selectedJobId);

  return (
    <div className="grid grid-cols-1 gap-4">
      <div className="col-span-full mt-4">
        {/* Instruction now moved to parent component */}
        
        {/* Status filter */}
        {onStatusChange && (
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">{t('jobs.filters.jobStatus')}</label>
            <div className="flex gap-2">
              <button
                onClick={() => onStatusChange('ACTIVE')}
                className={`px-3 py-1.5 rounded text-sm font-medium ${
                  jobStatus === 'ACTIVE' 
                    ? 'bg-blue-600 text-white' 
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                {t('jobs.filters.ongoing')}
              </button>
              <button
                onClick={() => onStatusChange('COMPLETED')}
                className={`px-3 py-1.5 rounded text-sm font-medium ${
                  jobStatus === 'COMPLETED' 
                    ? 'bg-purple-600 text-white' 
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                {t('jobs.filters.completed')}
              </button>
              <button
                onClick={() => onStatusChange('ALL')}
                className={`px-3 py-1.5 rounded text-sm font-medium ${
                  jobStatus === 'ALL' 
                    ? 'bg-gray-800 text-white' 
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                {t('jobs.filters.allJobs')}
              </button>
              
              {/* Refresh button to force update job list */}
              <button
                onClick={() => {
                  // Show refreshing state
                  setRefreshing(true);
                  
                  // Use the data refresh context to refresh jobs data
                  refreshJobs();
                  
                  // Manually refetch the data
                  const refreshData = async () => {
                    try {
                      const jobsData = await jobsApi.getAll();
                      setJobs(jobsData);
                      
                      // Get all candidates
                      const candidates = await candidatesApi.getAll();
                      
                      // Count candidates for each job
                      const counts: Record<string, number> = {};
                      jobsData.forEach(job => {
                        counts[job.id] = candidates.filter(c => c.jobId === job.id).length;
                      });
                      
                      setJobCandidateCounts(counts);
                    } catch (err) {
                      console.error('Failed to refresh data:', err);
                    } finally {
                      // Reset refreshing state after a short delay to show the animation
                      setTimeout(() => setRefreshing(false), 500);
                    }
                  };
                  
                  refreshData();
                }}
                className="px-2 py-1.5 rounded-full ml-1 text-sm font-medium bg-gray-100 text-gray-700 hover:bg-gray-200"
                title={t('common.refresh')}
                disabled={refreshing}
              >
                <svg xmlns="http://www.w3.org/2000/svg" className={`h-4 w-4 ${refreshing ? 'animate-spin' : ''}`} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M21 2v6h-6"></path>
                  <path d="M3 12a9 9 0 0 1 15-6.7L21 8"></path>
                  <path d="M3 22v-6h6"></path>
                  <path d="M21 12a9 9 0 0 1-15 6.7L3 16"></path>
                </svg>
              </button>
            </div>
          </div>
        )}

        <div className="relative">
          <select
            id="job-select"
            value={selectedJobId}
            onChange={(e) => onJobChange(e.target.value)}
            className={`w-full px-4 py-2.5 border rounded-lg focus:ring-2 focus:ring-blue-500 transition-shadow text-gray-900 appearance-none font-medium ${
              !selectedJobId ? 'bg-blue-50 border-blue-300' : 'bg-gradient-to-r from-blue-50 to-white border-blue-200 hover:border-blue-300'
            }`}
          >
            <option value="" className="text-gray-500 italic">
              {t('candidates.jobSelect.selectJob')}
            </option>
            
            {/* Active Jobs Group */}
            {filteredJobs.some(job => !job.status || job.status === 'ACTIVE') && (
              <optgroup label={t('jobs.filters.ongoing')} className="font-semibold">
                {filteredJobs
                  .filter(job => !job.status || job.status === 'ACTIVE')
                  .map((job) => (
                    <option key={job.id} value={job.id}>
                      • {job.job_title || job.title}
                    </option>
                  ))
                }
              </optgroup>
            )}
            
            {/* Completed Jobs Group */}
            {filteredJobs.some(job => job.status === 'COMPLETED') && (
              <optgroup label={t('jobs.filters.completed')} className="font-semibold">
                {filteredJobs
                  .filter(job => job.status === 'COMPLETED')
                  .map((job) => (
                    <option key={job.id} value={job.id}>
                      ✓ {job.job_title || job.title}
                    </option>
                  ))
                }
              </optgroup>
            )}
          </select>
          <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
            <svg className="h-5 w-5 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7"></path>
            </svg>
          </div>
        </div>
      </div>

      {selectedJob && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="bg-blue-50 rounded-lg p-4 border border-blue-100">
            <div className="flex items-center gap-3 text-blue-600">
              <Building2 className="w-5 h-5 flex-shrink-0" />
              <div className="min-w-0">
                <p className="text-sm font-medium">{t('candidates.jobSelect.department')}</p>
                <p className="text-lg font-semibold truncate">{selectedJob.department}</p>
              </div>
            </div>
          </div>

          <div className="bg-blue-50 rounded-lg p-4 border border-blue-100">
            <div className="flex items-center gap-3 text-blue-600">
              <Calendar className="w-5 h-5 flex-shrink-0" />
              <div className="min-w-0">
                <p className="text-sm font-medium">{t('candidates.jobSelect.dueDate')}</p>
                <p className="text-lg font-semibold truncate">
                  {selectedJob.application_deadline || selectedJob.dueDate
                    ? new Date(selectedJob.application_deadline || selectedJob.dueDate).toLocaleDateString() 
                    : t('candidates.jobSelect.noDeadline')}
                </p>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default JobSelect;